import React from 'react';
import { Shield, Lock, Eye, Database, UserCheck, AlertCircle } from 'lucide-react';

const AccountPrivacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-blue-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Shield size={48} />
            <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-xl text-blue-100">Your privacy and data security are our top priorities</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Last Updated */}
        <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-8">
          <p className="text-sm text-gray-700">
            <strong>Last Updated:</strong> January 28, 2026
          </p>
          <p className="text-sm text-gray-600 mt-1">
            This Privacy Policy explains how AV Mart Shamli collects, uses, and protects your personal information.
          </p>
        </div>

        {/* Introduction */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Commitment to Privacy</h2>
          <p className="text-gray-700 leading-relaxed">
            At AV Mart Shamli, we respect your privacy and are committed to protecting your personal data. 
            This privacy policy will inform you about how we handle your personal data when you visit our 
            website or use our mobile application, and tell you about your privacy rights and how the law 
            protects you.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Database className="text-blue-600" size={32} />
            <h2 className="text-3xl font-bold text-gray-800">Information We Collect</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Personal Information</h3>
              <p className="text-gray-700 mb-2">We collect the following personal information:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Identity Data:</strong> Name, username, date of birth</li>
                <li><strong>Contact Data:</strong> Email address, phone number, delivery address</li>
                <li><strong>Financial Data:</strong> Payment card details (processed securely through payment gateways)</li>
                <li><strong>Transaction Data:</strong> Order history, payment details</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
                <li><strong>Usage Data:</strong> How you use our website and services</li>
                <li><strong>Marketing Data:</strong> Your preferences for receiving marketing communications</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">How We Collect Information</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Direct Interactions</h4>
                  <p className="text-sm text-gray-600">When you create an account, place orders, or contact customer service</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Automated Technologies</h4>
                  <p className="text-sm text-gray-600">Cookies and similar tracking technologies as you navigate our site</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Third Parties</h4>
                  <p className="text-sm text-gray-600">Analytics providers, payment processors, and delivery partners</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How We Use Your Data */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <UserCheck className="text-green-600" size={32} />
            <h2 className="text-3xl font-bold text-gray-800">How We Use Your Data</h2>
          </div>

          <p className="text-gray-700 mb-4">We use your personal data for the following purposes:</p>

          <div className="space-y-4">
            <div className="flex gap-3">
              <span className="text-green-600 text-xl">✓</span>
              <div>
                <h4 className="font-semibold text-gray-800">Order Processing</h4>
                <p className="text-gray-700">To process and deliver your orders, manage payments, and provide customer support</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-green-600 text-xl">✓</span>
              <div>
                <h4 className="font-semibold text-gray-800">Account Management</h4>
                <p className="text-gray-700">To create and manage your account, including authentication and security</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-green-600 text-xl">✓</span>
              <div>
                <h4 className="font-semibold text-gray-800">Communication</h4>
                <p className="text-gray-700">To send order updates, delivery notifications, and respond to your inquiries</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-green-600 text-xl">✓</span>
              <div>
                <h4 className="font-semibold text-gray-800">Personalization</h4>
                <p className="text-gray-700">To provide personalized recommendations and improve your shopping experience</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-green-600 text-xl">✓</span>
              <div>
                <h4 className="font-semibold text-gray-800">Marketing</h4>
                <p className="text-gray-700">To send promotional offers and updates (only with your consent)</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-green-600 text-xl">✓</span>
              <div>
                <h4 className="font-semibold text-gray-800">Analytics</h4>
                <p className="text-gray-700">To analyze usage patterns and improve our services</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-green-600 text-xl">✓</span>
              <div>
                <h4 className="font-semibold text-gray-800">Legal Compliance</h4>
                <p className="text-gray-700">To comply with legal obligations and protect our rights</p>
              </div>
            </div>
          </div>
        </section>

        {/* Data Security */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="text-purple-600" size={32} />
            <h2 className="text-3xl font-bold text-gray-800">Data Security</h2>
          </div>

          <p className="text-gray-700 mb-6 leading-relaxed">
            We have implemented appropriate security measures to prevent your personal data from being 
            accidentally lost, used, or accessed in an unauthorized way, altered, or disclosed.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Technical Measures</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• SSL/TLS encryption for data transmission</li>
                <li>• Secure payment gateway integration</li>
                <li>• Regular security audits and updates</li>
                <li>• Access controls and authentication</li>
                <li>• Data encryption at rest</li>
              </ul>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Organizational Measures</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Limited access to personal data</li>
                <li>• Employee confidentiality agreements</li>
                <li>• Regular staff training on data protection</li>
                <li>• Incident response procedures</li>
                <li>• Vendor security assessments</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Your Rights */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Eye className="text-orange-600" size={32} />
            <h2 className="text-3xl font-bold text-gray-800">Your Privacy Rights</h2>
          </div>

          <p className="text-gray-700 mb-4">Under data protection laws, you have the following rights:</p>

          <div className="space-y-3">
            <div className="border-l-4 border-orange-500 pl-4 py-2">
              <h4 className="font-semibold text-gray-800">Right to Access</h4>
              <p className="text-gray-700">Request copies of your personal data</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-2">
              <h4 className="font-semibold text-gray-800">Right to Rectification</h4>
              <p className="text-gray-700">Request correction of inaccurate data</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-2">
              <h4 className="font-semibold text-gray-800">Right to Erasure</h4>
              <p className="text-gray-700">Request deletion of your personal data</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-2">
              <h4 className="font-semibold text-gray-800">Right to Restrict Processing</h4>
              <p className="text-gray-700">Request limitation on how we use your data</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-2">
              <h4 className="font-semibold text-gray-800">Right to Data Portability</h4>
              <p className="text-gray-700">Request transfer of your data to another service</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-2">
              <h4 className="font-semibold text-gray-800">Right to Object</h4>
              <p className="text-gray-700">Object to processing of your personal data</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4 py-2">
              <h4 className="font-semibold text-gray-800">Right to Withdraw Consent</h4>
              <p className="text-gray-700">Withdraw consent for marketing communications anytime</p>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-6">
            <p className="text-gray-700">
              <strong>To exercise any of these rights,</strong> please contact us at contactus@avmartshamli.com 
              or through your account settings. We will respond within 30 days.
            </p>
          </div>
        </section>

        {/* Cookies */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Cookies Policy</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We use cookies and similar tracking technologies to track activity on our service and store 
            certain information. You can instruct your browser to refuse all cookies or to indicate when 
            a cookie is being sent.
          </p>
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="font-semibold text-gray-800 mb-3">Types of Cookies We Use:</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
              <li><strong>Performance Cookies:</strong> Help us understand how visitors use our site</li>
              <li><strong>Functionality Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Marketing Cookies:</strong> Track your activity to show relevant ads (with consent)</li>
            </ul>
          </div>
        </section>

        {/* Data Retention */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Data Retention</h2>
          <p className="text-gray-700 leading-relaxed">
            We will retain your personal data only for as long as necessary for the purposes set out in this 
            privacy policy. We will retain and use your data to comply with legal obligations, resolve disputes, 
            and enforce our agreements. After account deletion, we may retain certain information for legal and 
            auditing purposes as required by law.
          </p>
        </section>

        {/* Third-Party Sharing */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Sharing Your Data</h2>
          <p className="text-gray-700 mb-4">We may share your personal data with:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li><strong>Delivery Partners:</strong> To fulfill your orders</li>
            <li><strong>Payment Processors:</strong> To process payments securely</li>
            <li><strong>Service Providers:</strong> Who help us operate our business (hosting, analytics, etc.)</li>
            <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
          </ul>
          <p className="text-gray-700 mt-4">
            We never sell your personal data to third parties for their marketing purposes.
          </p>
        </section>

        {/* Children's Privacy */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="text-red-600" size={32} />
            <h2 className="text-3xl font-bold text-gray-800">Children's Privacy</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            Our service is not intended for children under 18 years of age. We do not knowingly collect 
            personal information from children. If you are a parent or guardian and believe your child has 
            provided us with personal data, please contact us immediately.
          </p>
        </section>

        {/* Contact Information */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow-md p-8">
          <h2 className="text-3xl font-bold mb-4">Contact Us</h2>
          <p className="mb-4">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2">
            <p><strong>Email:</strong> contactus@avmartshamli.com</p>
            <p><strong>Phone:</strong> (+91-7251972000) (Mon-Sat, 9 AM - 6 PM)</p>
            <p><strong>Address:</strong> AV Mart Shamli, Near Jain Vihar, Majra Road, Shamli - 247776, India</p>
          </div>
          <div className="mt-6 pt-6 border-t border-blue-400">
            <p className="text-sm text-blue-100">
              We reserve the right to update this privacy policy at any time. We will notify you of any 
              changes by posting the new privacy policy on this page and updating the "Last Updated" date.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AccountPrivacy;
